import { Flex, Text, IconButton, Separator } from "@radix-ui/themes";
import { PlusIcon } from "lucide-react";
import Select from "@/components/shared/select";
import { EscalationRuleOperationType } from "@/v2/entities/escalation-rule/EscalationRule.model";
import { EscalationRuleFormData } from "../../EscalationRuleForm";
import { useCallback } from "react";

interface PrioritySectionProps {
  value: EscalationRuleFormData;
  onChange: (updates: Partial<EscalationRuleFormData>) => void;
  onRemove: () => void;
}

export function PrioritySection({
  value,
  onChange,
  onRemove,
}: PrioritySectionProps) {
  const handlePriorityChange = useCallback(
    (option: string) => {
      const existingPriorityOperation = value.escalationRuleOperations.find(
        (operation) =>
          operation.type === EscalationRuleOperationType.ADD_PRIORITY
      );

      if (existingPriorityOperation) {
        const updatedOperations = value.escalationRuleOperations.map(
          (operation) =>
            operation.type === EscalationRuleOperationType.ADD_PRIORITY
              ? { ...operation, value: option }
              : operation
        );
        onChange({ escalationRuleOperations: updatedOperations });
      } else {
        const newOperations = [
          ...value.escalationRuleOperations,
          {
            type: EscalationRuleOperationType.ADD_PRIORITY,
            enabled: true,
            key: null,
            value: option,
          },
        ];
        onChange({ escalationRuleOperations: newOperations });
      }
    },
    [value.escalationRuleOperations, onChange]
  );

  const currentPriority =
    value.escalationRuleOperations.find(
      (operation) => operation.type === EscalationRuleOperationType.ADD_PRIORITY
    )?.value ?? "medium";

  return (
    <>
      <Separator orientation="horizontal" size="4" />
      <Flex direction={"column"} gap={"2"} mt="4">
        <Flex justify="between" align="center">
          <Text weight="bold" size="2">
            Priority
          </Text>
          <IconButton color="gray" variant="ghost" size="1" onClick={onRemove}>
            <PlusIcon
              width={16}
              height={16}
              style={{ transform: "rotate(45deg)" }}
            />
          </IconButton>
        </Flex>
        <Text size="2" color="gray">
          Set the priority level for escalated tickets
        </Text>
        <Select
          options={[
            { label: "Urgent", value: "urgent" },
            { label: "High", value: "high" },
            { label: "Normal", value: "normal" },
            { label: "Low", value: "low" },
          ]}
          value={currentPriority}
          onChange={handlePriorityChange}
        />
      </Flex>
    </>
  );
}
